import Image from 'next/image';
import { A11y, Autoplay, Mousewheel } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Card, Flex, Heading, Text, useBreakpointValue } from '@chakra-ui/react';

import { useI18n } from '~/hooks/use-i18n';

import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/autoplay';

export type TestimonialCarouselProps = {
  testimonials: Array<{
    labelKey: string;
    quoteKey: string;
    title: string;
    name: string;
    imagePath: string;
    quoteColor?: string;
    labelColor?: string;
  }>;
  initialSlide?: number;
  onInteraction?: () => void;
};

export function TestimonialCarousel({ testimonials, initialSlide = 3, onInteraction }: TestimonialCarouselProps) {
  const { t, i18n } = useI18n();
  const cardSize = useBreakpointValue(
    {
      base: {
        width: '302px',
        height: '480px',
      },
      md: {
        width: '340px',
        height: '540px',
      },
    },
    { fallback: 'md' }
  );
  const isMobile = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    { fallback: 'md' }
  );

  return (
    <Box margin='0 auto' maxWidth='1728px' position='relative' width='100%'>
      <Box
        isolation='isolate'
        sx={{
          '.swiper .swiper-slide': {
            height: 'auto',
            display: 'flex',
          },
        }}
      >
        <Swiper
          key={isMobile ? 'mobile' : 'desktop'}
          direction='horizontal'
          initialSlide={initialSlide}
          longSwipes={false}
          longSwipesMs={115}
          loopedSlides={!isMobile ? 2 : undefined}
          modules={[A11y, Mousewheel, Autoplay]}
          slidesPerView='auto'
          spaceBetween={24}
          autoplay={{
            delay: 4000,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
            stopOnLastSlide: false,
            waitForTransition: true,
          }}
          mousewheel={{
            forceToAxis: true,
            releaseOnEdges: true,
            thresholdDelta: 10,
          }}
          centeredSlides
          loop
          onAutoplayStop={() => {
            // Note: autoplay stops when the user interacts with the carousel.
            onInteraction?.();
          }}
          onBeforeInit={(swiper) => {
            swiper.changeLanguageDirection(i18n.dir());
          }}
        >
          {testimonials.map((testimonial) => (
            <SwiperSlide
              key={testimonial.name}
              style={{
                width: cardSize?.width,
                height: cardSize?.height,
              }}
            >
              <Card
                border='1px solid'
                borderColor='hsla(0, 0%, 100%, 1)'
                boxShadow='none'
                overflow='hidden'
                position='relative'
                width='100%'
              >
                <Image
                  alt=''
                  loading='lazy'
                  sizes='340px'
                  src={testimonial.imagePath}
                  style={{ objectFit: 'cover', pointerEvents: 'none' }}
                  fill
                />
                <Flex
                  flexDirection='column'
                  inset={0}
                  justifyContent='space-between'
                  position='absolute'
                  padding={{
                    base: '22px',
                    md: '28px 30px',
                  }}
                >
                  <Flex
                    alignItems='center'
                    gap={4}
                    _after={{
                      content: "''",
                      display: 'block',
                      width: '100%',
                      height: '0.5px',
                      backgroundColor:
                        testimonial.labelColor === 'light' ? 'hsla(255, 100%, 100%, 0.66)' : 'hsla(0, 0%, 0%, 0.66)',
                      flex: 1,
                    }}
                  >
                    <Heading
                      as='p'
                      fontFamily='condensed'
                      fontSize='0.9375rem'
                      fontWeight='500'
                      lineHeight='0.9375'
                      textTransform='uppercase'
                      color={
                        testimonial.labelColor === 'light' ? 'hsla(255, 100%, 100%, 0.66)' : 'hsla(0, 0%, 0%, 0.66)'
                      }
                    >
                      {t(testimonial.labelKey)}
                    </Heading>
                  </Flex>
                  <Flex
                    flexDirection='column'
                    gap={{
                      base: 4,
                      md: 5,
                    }}
                  >
                    <Box
                      as='blockquote'
                      color={testimonial.quoteColor === 'light' ? 'white' : 'black'}
                      lineHeight='1.4'
                      fontSize={{
                        base: '0.875rem',
                        md: '0.9375rem',
                      }}
                    >
                      &ldquo;{t(testimonial.quoteKey)}&rdquo;
                    </Box>
                    <Flex>
                      <Box as='footer' color={testimonial.quoteColor === 'light' ? 'white' : 'black'}>
                        <Heading as='p' fontSize='1.125rem' fontWeight='600' lineHeight='1.1' mb={1}>
                          {testimonial.name}
                        </Heading>
                        <Text
                          color={testimonial.quoteColor === 'light' ? 'white' : 'black'}
                          fontSize='0.8125rem'
                          lineHeight='1.1'
                          opacity={0.66}
                        >
                          {testimonial.title}
                        </Text>
                      </Box>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
      <Box
        bgGradient='linear(to left, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 1) 100%)'
        bottom={0}
        height='100%'
        left={0}
        pointerEvents='none'
        position='absolute'
        top={0}
        width='300px'
        display={{
          base: 'none',
          '2xl': 'block',
        }}
      />
      <Box
        bgGradient='linear(to right, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 1) 100%)'
        bottom={0}
        height='100%'
        pointerEvents='none'
        position='absolute'
        right={0}
        top={0}
        width='300px'
        display={{
          base: 'none',
          '2xl': 'block',
        }}
      />
    </Box>
  );
}
